import i18n from "i18next";
import {initReactI18next} from "react-i18next";

import {moment} from "@/src/modules/core/lib/moment";
import {getStorageItem} from "@/src/modules/core/lib/storage";
import enCatalog from "@/src/locales/en.json";
import esCatalog from "@/src/locales/es.json";

export type AppLocale = "en" | "es";

const namespaces = Object.keys(enCatalog);

export function parseAppLocale(value: unknown): AppLocale {
  return value === "es" ? "es" : "en";
}

export function parseLocaleOverride(value: unknown): AppLocale | null {
  if (value === "en" || value === "es") {
    return value;
  }
  return null;
}

/** Last effective locale from persisted me store — used before GET /me on cold start. */
function readBootAppLocale(): AppLocale {
  if (typeof window === "undefined") {
    return "en";
  }

  try {
    const persisted = getStorageItem("me-store") as
      | {state?: {me?: {locale?: unknown} | null}}
      | null;
    return parseAppLocale(persisted?.state?.me?.locale);
  } catch {
    return "en";
  }
}

const bootLocale = readBootAppLocale();

void i18n.use(initReactI18next).init({
  lng: bootLocale,
  fallbackLng: "en",
  defaultNS: "common",
  ns: namespaces,
  resources: {
    en: enCatalog,
    es: esCatalog,
  },
  interpolation: {escapeValue: false},
});

moment.locale(bootLocale);
if (typeof document !== "undefined") {
  document.documentElement.lang = bootLocale;
}

export async function setAppLocale(locale: AppLocale): Promise<void> {
  await i18n.changeLanguage(locale);
  moment.locale(locale);
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
}

/** Vendored catalogs use Laravel `:placeholder` on some keys (e.g. dar.sheet, dar.email). */
export function interpolateCatalog(
  template: string,
  vars: Record<string, string | number>,
): string {
  let out = template;
  for (const [key, value] of Object.entries(vars)) {
    out = out.replaceAll(`:${key}`, String(value));
  }
  return out;
}

export default i18n;
